"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"

import { logout as apiLogout } from "../api/auth"
import { accessTokenStore } from "../api/token-store"
import { unsubscribeFromPush } from "../push"
import { purgeQueryCache } from "../query-client"
import type { OtpChannel } from "../api/types"
import type { Gender, Member } from "../types"

/**
 * Admin-supplied profile data carried from an invitation accept into
 * profile-setup. Gender is null when the admin left it blank, so the form
 * doesn't pre-select a gender the admin never chose.
 */
export interface InvitePrefill {
  name: string
  dob: string
  gender: Gender | null
}

interface AuthState {
  member: Member | null
  hasSeenTutorial: boolean
  /** Channel + identifier stashed across register start → otp → set-password → profile. */
  signupDraft: { channel: OtpChannel; identifier: string } | null
  /** Prefill from an invitation accept, consumed by profile-setup. */
  invitePrefill: InvitePrefill | null

  completeTutorial: () => void
  /** Set the member from a real API response (see lib/api/map.ts). */
  setMember: (member: Member) => void
  signOut: () => void

  startSignup: (channel: OtpChannel, identifier: string) => void
  clearSignupDraft: () => void

  setInvitePrefill: (prefill: InvitePrefill) => void
  clearInvitePrefill: () => void

  /** Local-only profile patch — used by the settings edit screen (not yet API-backed). */
  updateProfile: (patch: Partial<Member>) => void
}

/**
 * Profile setup writes name, username, date of birth and gender, and its submit
 * button requires all four — so a member missing any of them never finished it.
 */
export function profileIncomplete(member: Member) {
  return (
    !member.fullName.trim() ||
    !member.username.trim() ||
    !member.dob ||
    !member.gender
  )
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      member: null,
      hasSeenTutorial: false,
      signupDraft: null,
      invitePrefill: null,

      completeTutorial: () => set({ hasSeenTutorial: true }),

      setMember: (member) => set({ member }),

      signOut: () => {
        // Drop this device's push subscription BEFORE revoking the token: the
        // backend DELETE /push/subscribe is scoped to the current user, so it
        // must run while this member's bearer is still valid. Otherwise the
        // endpoint stays mapped to this member and their pushes keep landing on
        // the next member who logs in on this device.
        // The clear lives in `finally`: if either step throws, the token must
        // still go, or the next launch revalidates it via /auth/me and bounces
        // the "signed out" user straight back to /home.
        void (async () => {
          try {
            await unsubscribeFromPush()
            // Best-effort server-side token revoke; clears the token locally too.
            await apiLogout()
          } finally {
            accessTokenStore.clear()
          }
        })()
        // Drop SW-cached API responses (membership/QR) so the next member on this
        // device can't read them offline.
        if (typeof navigator !== "undefined") {
          navigator.serviceWorker?.controller?.postMessage({
            type: "clear-api-cache",
          })
        }
        // Same reason: purge the persisted react-query cache so the next member
        // on this device doesn't see this member's data flash on reopen.
        purgeQueryCache()
        set({ member: null, signupDraft: null, invitePrefill: null })
      },

      startSignup: (channel, identifier) =>
        set({ signupDraft: { channel, identifier } }),
      clearSignupDraft: () => set({ signupDraft: null }),

      setInvitePrefill: (prefill) => set({ invitePrefill: prefill }),
      clearInvitePrefill: () => set({ invitePrefill: null }),

      updateProfile: (patch) =>
        set((s) => (s.member ? { member: { ...s.member, ...patch } } : s)),
    }),
    { name: "psc-auth" }
  )
)
